import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { acceptAll } from '../store/cookieConsentSlice';

interface CookieBannerProps {
  flowerImagePath: string;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ flowerImagePath }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { consent } = useSelector((state: RootState) => state.cookieConsent);
  const user = useSelector((state: RootState) => state.auth.user);
  const currentLanguage = useSelector((state: RootState) => state.language.currentLanguage);

  if (consent.hasInteracted) {
    return null;
  }


  const handleSubscriptionClick = (action: 'signup' | 'upgrade') => {
    // Automatically accept all cookies when user selects a plan
    dispatch(acceptAll());
    
    if (action === 'signup') {
      navigate('/signup');
    } else if (action === 'upgrade') {
      navigate(user ? '/profile?tab=subscription' : '/signup');
    }
  };

  const handleLoginClick = () => {
    dispatch(acceptAll());
    navigate('/login');
  };

  const subscriptionContent = {
    en: {
      startFree: "Start your up to 90‑day free trial",
      freeDesc: "Create a free account to start your up to 90‑day trial — no credit card required. The trial ends automatically.",
      freeFeatures: [
        "Latest studies matching your specialty",
        "Business skills for doctors",
        "Current science from Austria"
      ],
      alreadyHaveAccount: "Already have an account?",
      loginHere: "Login here"
    },
    de: {
      startFree: "Jetzt bis zu 90‑Tage‑Testphase starten",
      freeDesc: "Erstellen Sie ein kostenloses Konto, um Ihre bis zu 90‑Tage‑Testphase zu starten — keine Kreditkarte erforderlich. Die Testphase endet automatisch.",
      freeFeatures: [
        "Neueste Studien passend zu Ihrem Fachgebiet",
        "Business Skills für Ärzte",
        "Aktuelle Wissenschaft aus Österreich"
      ],
      alreadyHaveAccount: "Haben Sie bereits ein Konto?",
      loginHere: "Hier anmelden"
    }
  };

  const texts = subscriptionContent[currentLanguage === 'de' ? 'de' : 'en'];

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-3 md:p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-4xl relative overflow-hidden rounded-lg max-h-[90vh] overflow-y-auto">
        <div className="relative z-20 p-4 md:p-8">
          {/* Single CTA: Free up to 90-day trial */}
          <div className="grid grid-cols-1 gap-4 md:gap-8">
            <div className="bg-gray-50 p-4 md:p-6 rounded-lg flex flex-col h-full">
              <div>
                <h3 className="text-lg md:text-2xl font-bold text-[#49ABDB] mb-3 md:mb-4 leading-tight">
                  {texts.startFree}
                </h3>
                <p className="text-sm md:text-base text-gray-700 mb-4 md:mb-6">
                  {texts.freeDesc}
                </p>
                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                  {texts.freeFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2 text-[#49ABDB] text-base md:text-lg flex-shrink-0">✓</span>
                      <span className="text-gray-700 text-sm md:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto pt-3 md:pt-4">
                <button
                  onClick={() => handleSubscriptionClick('signup')}
                  className="w-full px-3 md:px-4 py-2 bg-[#49ABDB] text-white rounded hover:bg-[#3d8fb8] transition-colors text-sm md:text-base"
                >
                  {texts.startFree}
                </button>
              </div>
            </div>
          </div>

          {/* Login Link Section */}
          <div className="text-center mt-6 pt-4 border-t border-gray-200">
            <p className="text-gray-600 text-sm mb-2">{texts.alreadyHaveAccount}</p>
            <button
              onClick={handleLoginClick}
              className="text-[#49ABDB] hover:underline text-sm font-medium"
            >
              {texts.loginHere}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
